import { useState, useRef, useEffect, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { AddCircleOutline, RemoveCircleOutline, FlightTakeoff, FlightLand, LocationOn, Phone, Forum } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import 'intl-tel-input/build/css/intlTelInput.css';
import intlTelInput from 'intl-tel-input';
import ready from 'document-ready';
import '@Styles/vendors-extensions/_intl-tel-input.scss';

import TextField from '@Components/Shared/Controls/TextField'
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import atoms from '@Components/Utilities/SandhillsAtoms';
import DateRangePicker from '@Components/MuiWrappers/DateRangePicker';
import QuoteConfirmationModal from '@Components/Shared/Modals/QuoteConfirmationModal ';
import { sandhillsRequest, focusErroredField, insertURLParameter } from '@Components/Utilities/SandhillsUtility';
import { CharterFlightType } from '@Components/Utilities/SandhillsConstants';
import EmptyLegAlertModal from '@Components/Shared/Modals/EmptyLegAlertModal';



const RequestACharter = (props) => {
    const routes = useRecoilValue(atoms.routes);
    const inputReferences = useRef({});
    const itiInstanceRef = useRef(null);
    const timeout = useRef(null);
    const [success, setSuccess] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [showConfirmation, setShowConfirmation] = useState(false);
    const currentUser = useRecoilValue(atoms.currentUser);
    const toggleConfirmationModal = () => {
        setShowConfirmation(showConfirmation => !showConfirmation);
    }
    const sitePhoneFormatCultureCode = useRecoilValue(atoms.sitePhoneFormatCultureCode);
    const [shouldRedirect, setShouldRedirect] = useState(props.TryRedirectSave);
    const [userError, setUserError] = useState(false);
    const [userErrorMessage, setUserErrorMessage] = useState('');
    const [responseError, setResponseError] = useState(props.ErrorMessage || '');
    const [isTestOverride, setIsTestOverride] = useState(props.IsTestOverride || false);
    const distanceOptions = [10, 20, 50, 150, 200];
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [emptyLegAlerts, setEmptyLegAlerts] = useState(props.EmptyLegAlerts ? props.EmptyLegAlerts : []);

    const [formData, setFormData] = useState({
        TypeOfFlight: props.TypeOfFlight ? props.TypeOfFlight : CharterFlightType.RoundTrip,
        DepartingDate: props.DepartingDate ? new Date(props.DepartingDate) : null,
        ReturningDate: props.ReturningDate ? new Date(props.ReturningDate) : null,
        NumberOfPassenger: props.NumberOfPassenger || '',
        FromCityAirportCode: props.FromCityAirportCode || null,
        ToCityAirportCode: props.ToCityAirportCode || null,
        Phone: '',
        Comments: '',
    });

    const [emptyLegFormData, setEmptyLegFormData] = useState({
        DepartingDate: props.DepartingDate ? new Date(props.DepartingDate) : null,
        ReturningDate: props.ReturningDate ? new Date(props.ReturningDate) : null,
        NumberOfPassenger: props.NumberOfPassenger || '',
        CharterSearchCityAirportCode: props.CharterSearchCityAirportCode || null,
        Distance: props.Distance || ''
    });

    useEffect(() => {
        if (shouldRedirect) {
            onSubmit();
            setShouldRedirect(false);
        }

        ready(() => {
            const racData = sessionStorage.getItem('charterRequestFormData');
            if (racData) {
                const parsed = JSON.parse(racData);
                setFormData(previousState => ({
                    ...previousState,
                    ...parsed,
                    DepartingDate: parsed.DepartingDate ? new Date(parsed.DepartingDate) : null,
                    ReturningDate: parsed.ReturningDate ? new Date(parsed.ReturningDate) : null,
                }));
                sessionStorage.removeItem('charterRequestFormData');
            }


            const phoneElement = document.querySelector('.request-a-charter #Phone');
            if (phoneElement) {
                itiInstanceRef.current = intlTelInput(phoneElement, {
                    autoHideDialCode: false,
                    autoPlaceholder: 'off',
                    nationalMode: false,
                    separateDialCode: true,
                    preferredCountries: ['us', 'gb', 'ca'],
                    initialCountry: sitePhoneFormatCultureCode ? sitePhoneFormatCultureCode.substring(sitePhoneFormatCultureCode.length - 2) : 'us'
                });
            }
            const updateLabelPadding = () => {
                const flagContainer = phoneElement.closest('.iti')?.querySelector('.iti__flag-container');
                const label = phoneElement.closest('.input-container')?.querySelector('label');
                if (flagContainer && label) {
                    label.style.paddingLeft = `${flagContainer.offsetWidth + 5}px`;
                }
            };

            updateLabelPadding();
            phoneElement.addEventListener('countrychange', updateLabelPadding);

        })
    }, []);

    const submitForm = (e) => {
        e.preventDefault();
        let errors = run(formData, [
            ruleRunner('TypeOfFlight', translator.translate('sandhills.typeofflight')),
            ruleRunner('DepartingDate', translator.translate('sandhills.datefrom'), ValidationRules.required),
            ruleRunner('ReturningDate', translator.translate('sandhills.dateto')),
            ruleRunner('NumberOfPassenger', translator.translate('sandhills.NumberOfPassenger'), ValidationRules.required, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(1, false)),
            ruleRunner('FromCityAirportCode', translator.translate('sandhills.FromCityAirportCode'), ValidationRules.required),
            ruleRunner('ToCityAirportCode', translator.translate('sandhills.ToCityAirportCode'), ValidationRules.required)
        ]);

        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);
        setSubmitted(false);

        if (Object.keys(errors).length <= 0) {
            setSuccess(false);
            setDisableSubmit(true);
            if (!currentUser.IsAuthenticated) {
                sessionStorage.setItem('charterRequestFormData', JSON.stringify(formData));
                window.location.href = routes.SSO + '?to=' + encodeURIComponent(window.location.href);
            }
            else {
                onSubmit();
            }
        }
    };

    const onSubmit = (token) => {
        var selectedCountry = itiInstanceRef.current?.getSelectedCountryData() || {};
        var dialCode = selectedCountry.dialCode ? "+" + selectedCountry.dialCode : "";
        var countryCode = selectedCountry.iso2?.toUpperCase() || "";

        sandhillsRequest({
            url: routes.CharterRequestSave,
            method: 'post',
            data: {
                TypeOfFlight: formData.TypeOfFlight,
                EquipmentMessageStatus: props.EquipmentMessageStatus,
                DepartingDate: formData.DepartingDate,
                ReturningDate: formData.ReturningDate,
                FromCityAirportCode: formData.FromCityAirportCode,
                ToCityAirportCode: formData.ToCityAirportCode,
                NumberOfPassenger: formData.NumberOfPassenger,
                PhoneNumber: dialCode + formData.Phone,
                PhoneCountryCode: countryCode,
                Comments: formData.Comments
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            if (data.Success) {
                setIsTestOverride(data?.IsTestOverride);
                setShowConfirmation(true);
                setResponseError(null);
                setFormData(previousState => {
                    return {
                        ...previousState,
                        SelectedAircraftPrefType: [],
                        TypeOfFlight: CharterFlightType.RoundTrip,
                        DepartingDate: null,
                        ReturningDate: null,
                        FromCityAirportCode: null,
                        ToCityAirportCode: null,
                        NumberOfPassenger: 1,
                        Comments: ''
                    };
                });
            } else {
                setResponseError(data?.ErrorMessage);
                if (props.IsEmployee && !isTestOverride) {
                    setUserError(true);
                    setUserErrorMessage(<p className='error'>{translator.translate('sandhills.quoterequestemployee')}</p>);
                    setShowConfirmation(true);
                }
                else if (!currentUser.IsAuthenticated && !props.IsEmployee && !isTestOverride) {
                    setUserError(true);
                    setUserErrorMessage(<p className='error'>{translator.translate('sandhills.quoterequestlogin')}</p>);
                    setShowConfirmation(true);
                }
            }

            setSuccess(data.Success);
            setSubmitted(true);
            setDisableSubmit(false);
            setValidationErrors(data.ValidationErrors || {});
            focusErroredField(data.ValidationErrors, inputReferences);
        });
    };

    const closeModalTimer = () => {
        setShowConfirmation(false);
    };

    const getAutoCompleteUrl = (e, url) => {
        return url;
    };

    const getAutoCompleteData = (e) => {
        return {
            text: e.target.value
        };
    };

    const onChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
    };

    const onAirportChange = (e) => {
        let id = e.target.id;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [id]: { AirportCodeDisplay: value } };
        });
    }

    const onEmptyLegAirportChange = (e) => {
        let id = e.target.id;
        let value = e.target.value;
        setEmptyLegFormData(previousState => {
            return { ...previousState, [id]: { AirportCodeDisplay: value } };
        });
    }

    const handleDecrement = () => {
        if (formData.NumberOfPassenger <= 1) {
            return;
        }

        setFormData(previousState => {
            return { ...previousState, NumberOfPassenger: previousState.NumberOfPassenger - 1 };
        });
    }

    const handleIncrement = () => {
        setFormData(previousState => {
            return { ...previousState, NumberOfPassenger: previousState.NumberOfPassenger + 1 };
        });
    }

    const EmptyLegHandleDecrement = () => {
        if (emptyLegFormData.NumberOfPassenger <= 1) {
            return;
        }

        setEmptyLegFormData(previousState => {
            return { ...previousState, NumberOfPassenger: previousState.NumberOfPassenger - 1 };
        });
    }

    const EmptyLegHandleIncrement = () => {
        setEmptyLegFormData(previousState => {
            return { ...previousState, NumberOfPassenger: previousState.NumberOfPassenger + 1 };
        });
    }

    const setDateRanges = (startDate, endDate) => {
        setFormData(previousState => {
            return { ...previousState, DepartingDate: startDate, ReturningDate: endDate };
        });
    }

    const setEmptyLegDateRanges = (startDate, endDate) => {
        setEmptyLegFormData(previousState => {
            return { ...previousState, DepartingDate: startDate, ReturningDate: endDate };
        });
    }

    const setFlightType = (e) => {
        setFormData(previousState => {
            return { ...previousState, TypeOfFlight: e.target.value };
        });
    }

    const flightSearch = (e) => {
        e.preventDefault();
        let url = routes.EmptyLegsDirectory;

        if (emptyLegFormData.Distance !== '') {
            if (emptyLegFormData.CharterSearchCityAirportCode?.AirportCityID > 0) {
                url = insertURLParameter(url, 'CityID', emptyLegFormData.CharterSearchCityAirportCode.AirportCityID);
            }
            else if (emptyLegFormData.CharterSearchCityAirportCode?.AirportCodeID > 0) {
                url = insertURLParameter(url, 'AirportID', emptyLegFormData.CharterSearchCityAirportCode.AirportCodeID);
            }
            else {
                console.log('Invalid City/Airport. Select value from dropdown or remove airport and distance.');
                return;
            }

            url = insertURLParameter(url, 'Distance', emptyLegFormData.Distance);
        }
        if (emptyLegFormData.DepartingDate) {
            url = insertURLParameter(url, 'DepartureDate', emptyLegFormData.DepartingDate.toISOString().split('T')[0] + '*' + emptyLegFormData.DepartingDate.toISOString().split('T')[0]);
        }

        window.location.href = url;
    };

    const emptyLegAlertButtonClick = () => {
        if (!currentUser.IsAuthenticated) {
            window.location.href = routes.EmptyLegsAlerts;
        }

        else {
            setShowAlertModal(true);
        }
    }

    return (
        <div className='request-a-charter'>
            <h1>{translator.translate('Sandhills.Tradesites.MVC.CharterHubHomeH1')}</h1>
            {showConfirmation &&
                <QuoteConfirmationModal
                    onClose={closeModalTimer}
                    toggleConfirmationModal={toggleConfirmationModal}
                    title={isTestOverride ? "Request A Charter - Employee Test" : translator.translate('sandhills.tradesites.mvc.requestaprivatecharterflightquote')}
                    message={isTestOverride ? "Test Charter Quote Email Lead Was Succcessfully Sent" : userError ? userErrorMessage : translator.translate('sandhills.quoterequestsent')} />
            }
            <div className='container'>
                <div id='raqform' className='form'>
                    <h2><TranslationText tKey='sandhills.tradesites.mvc.requestaprivatecharterflightquote' /></h2>
                    <div className='request-form-subtext'>
                        Instant access to aircraft from certified operators.{!currentUser?.IsAuthenticated && (
                            <>
                                {' '}<a href={routes.FullRegistrationRoute} style={{ fontWeight: 700 }}>Click Here</a> to log in and get started.
                            </>
                        )}
                    </div>
                    {responseError && <p className='error'>{responseError}</p>}
                    <div className='form-row'>
                        <div className="trip-selector">
                            <a
                                className={`two-way ${formData.TypeOfFlight === CharterFlightType.RoundTrip ? 'active' : ''}`}
                                onClick={() => setFlightType({ target: { value: CharterFlightType.RoundTrip } })}
                            >
                                Round-Trip
                            </a>
                            <a
                                className={`one-way ${formData.TypeOfFlight === CharterFlightType.OneWay ? 'active' : ''}`}
                                onClick={() => setFlightType({ target: { value: CharterFlightType.OneWay } })}
                            >
                                One-Way
                            </a>
                        </div>
                    </div>
                    <div className='quote-form'>
                        <div className='form-flex'>
                            <div className='form-row form-bg'>
                                <div className="row first-row">
                                    <div className='form-col'>
                                        <div className='form-group'>
                                            <FlightTakeoff className='material-icon' />
                                            <TextField
                                                id='FromCityAirportCode'
                                                name='FromCityAirportCode'
                                                floatingLabel={translator.translate('sandhills.tradesites.mvc.cityairportfrom')}
                                                value={formData.FromCityAirportCode?.AirportCodeDisplay}
                                                type='text'
                                                class='input-container'
                                                required={true}
                                                ref={(element) => inputReferences.current[`FromCityAirportCode`] = element}
                                                updateField={(value, airportcodeid, cityid) => setFormData(previousState => {
                                                    return { ...previousState, FromCityAirportCode: { AirportCodeID: airportcodeid, AirportCityID: cityid, AirportCodeDisplay: value } };
                                                })}
                                                onFieldChanged={onAirportChange}
                                                autoComplete={{
                                                    ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetCityAirportCodeAutoComplete),
                                                    postData: (e) => getAutoCompleteData(e)
                                                }}
                                            />
                                        </div>
                                        <span className='error'>{validationErrors.FromCityAirportCode}</span>
                                    </div>
                                    <div className='form-col'>
                                        <div className='form-group'>
                                            <FlightLand className='material-icon' />
                                            <TextField
                                                id='ToCityAirportCode'
                                                name='ToCityAirportCode'
                                                floatingLabel={translator.translate('sandhills.tradesites.mvc.cityairportto')}
                                                value={formData.ToCityAirportCode?.AirportCodeDisplay}
                                                type='text'
                                                class='input-container'
                                                required={true}
                                                ref={(element) => inputReferences.current[`ToCityAirportCode`] = element}
                                                onFieldChanged={onAirportChange}
                                                updateField={(value, airportcodeid, cityid) => setFormData(previousState => {
                                                    return { ...previousState, ToCityAirportCode: { AirportCodeID: airportcodeid, AirportCityID: cityid, AirportCodeDisplay: value } };
                                                })}
                                                autoComplete={{
                                                    ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetCityAirportCodeAutoComplete),
                                                    postData: (e) => getAutoCompleteData(e)
                                                }}
                                            />
                                        </div>
                                        <span className='error'>{validationErrors.ToCityAirportCode}</span>
                                    </div>
                                    <div className='form-col'>
                                        <div className='form-group'>
                                            <DateRangePicker departingDate={formData.DepartingDate} returningDate={formData.ReturningDate} setDateRanges={setDateRanges} isRoundTrip={formData.TypeOfFlight == CharterFlightType.RoundTrip} validationErrors={validationErrors.DepartingDate} />
                                        </div>
                                        <span className='error'>{validationErrors.DepartingDate}</span>
                                    </div>
                                    <div className='form-col phone'>
                                        <div className='form-group phone'>
                                            <Phone className='material-icon' />
                                            <TextField
                                                type='text'
                                                id='Phone'
                                                name='Phone'
                                                value={formData.Phone}
                                                required={true}
                                                floatingLabel={translator.translate('sandhills.phone')}
                                                validationErrors={validationErrors.phone}
                                                ref={(element) => inputReferences.current[`Phone`] = element}
                                                class='input-container'
                                                onFieldChanged={onChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row second-row">
                                    <div className="form-col">
                                        <div className='form-group'>
                                            <Forum className='material-icon' />
                                            <TextField
                                                type='text'
                                                id='Comments'
                                                name='Comments'
                                                value={formData.Comments}
                                                required={false}
                                                floatingLabel={translator.translate('sandhills.tradepubwebsite.mvc.comments')}
                                                ref={(element) => inputReferences.current[`Comments`] = element}
                                                class='input-container'
                                                onFieldChanged={onChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="button-group">
                                        <div className='form-col passenger-selector'>
                                            <div className='number-counter'>
                                                <RemoveCircleOutline onClick={handleDecrement} className={`number-counter-button ${formData.NumberOfPassenger <= 1 ? 'disabled' : ''}`} />
                                                <label>{formData.NumberOfPassenger} <TranslationText tKey='sandhills.passengers' /></label>
                                                <AddCircleOutline onClick={handleIncrement} className='number-counter-button'/>
                                            </div>
                                        </div>
                                        <input className='ts-button rac-button' type='submit' id='request-a-charter-submit-button' name='request-a-charter-submit-button' value={translator.translate('sandhills.tradepubwebsite.mvc.submit')} disabled={disableSubmit} onClick={submitForm} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr />
                    <h2><TranslationText tKey='sandhills.tradesites.mvc.exclusiveemptylegdeals' /></h2>
                    <div className='request-form-subtext'>
                        Limited flights · Limited seats · Limited time. Browse now and book before they’re gone
                    </div>
                    <div class="el-btn-container">
                        <button className="ts-rounded-button alert-button" onClick={emptyLegAlertButtonClick}><i className="fa-solid fa-bell"></i>Set Up Alerts</button>
                        <a className="request-form-empty-leg-link" href={routes.EmptyLegsDirectory}>Browse All Empty Legs</a>
                    </div>
                    <div className='quote-form'>
                        <div className='form-flex'>
                            <div className='form-row form-bg'>
                                <div className="row third-row">
                                    <div className='form-col airport-selector'>
                                        <div className='form-group icon-group plane-up'>
                                            <FlightTakeoff className='material-icon' />
                                            <TextField
                                                id='CharterSearchCityAirportCode'
                                                name='CharterSearchCityAirportCode'
                                                floatingLabel={translator.translate('sandhills.tradesites.mvc.cityairportfrom')}
                                                value={emptyLegFormData.CharterSearchCityAirportCode?.AirportCodeDisplay}
                                                type='text'
                                                class='input-container'
                                                required={true}
                                                ref={(element) => inputReferences.current[`CharterSearchCityAirportCode`] = element}
                                                updateField={(value, airportcodeid, cityid) => setEmptyLegFormData(previousState => {
                                                    return { ...previousState, CharterSearchCityAirportCode: { AirportCodeID: airportcodeid, AirportCityID: cityid, AirportCodeDisplay: value } };
                                                })}
                                                onFieldChanged={onEmptyLegAirportChange}
                                                autoComplete={{
                                                    ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetCityAirportCodeAutoComplete),
                                                    postData: (e) => getAutoCompleteData(e)
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className='form-col'>
                                        <div className='form-group'>
                                            <LocationOn className='material-icon' />
                                            <div className='select-container'>                                           
                                                <select
                                                    id='distance-select'
                                                    name='Distance'
                                                    value={emptyLegFormData.Distance || ''}
                                                    onChange={(e) => setEmptyLegFormData(previousState => ({
                                                        ...previousState,
                                                        Distance: e.target.value
                                                    }))}
                                                    required
                                                >
                                                    <option value=''></option>
                                                    {distanceOptions.map((distance) => (
                                                        <option key={distance} value={distance}>
                                                            {distance} {props.IsMetric ? translator.translate('sandhills.tradepubwebsite.mvc.kilometersabrv') : translator.translate('sandhills.tradepubwebsite.mvc.miles')}
                                                        </option>
                                                    ))}
                                                </select>
                                                <label className={emptyLegFormData.Distance ? 'active' : ''}>{translator.translate('sandhills.tradesites.mvc.distancefromairport')}</label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='form-col'>
                                        <div className='form-group'>
                                            <DateRangePicker departingDate={emptyLegFormData.DepartingDate} setDateRanges={setEmptyLegDateRanges} validationErrors={validationErrors.elDepartDate} />
                                        </div>
                                    </div>
                                    <div className="button-group">
                                        <div className='form-col passenger-selector'>
                                            <div className='number-counter'>
                                                <RemoveCircleOutline onClick={EmptyLegHandleDecrement} className={`number-counter-button ${emptyLegFormData.NumberOfPassenger <= 1 ? 'disabled' : ''}`} />
                                                <label>{emptyLegFormData.NumberOfPassenger} <TranslationText tKey='sandhills.passengers' /></label>
                                                <AddCircleOutline onClick={EmptyLegHandleIncrement} className='number-counter-button'/>
                                            </div>
                                        </div>

                                        <button className='ts-button rac-button' type='submit' id='empty-leg-submit-button' name='empty-leg-submit-button' disabled={disableSubmit} onClick={flightSearch}>
                                            <SearchIcon /><TranslationText tKey='sandhills.search' />
                                        </button>
                                    </div>
                                </div>                                   
                            </div>
                        </div>
                    </div>
                    {disableSubmit && <LoadingRing backdrop />}
                </div>
            </div>
            {showAlertModal &&
                <EmptyLegAlertModal
                    onClose={() => setShowAlertModal(false)}
                />
            }
        </div>
    );
};

export default RequestACharter;